import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../index";

const SiumAherProgress = ({ takenPercent, totalPieces, takenPieces, learnedPercent, learnedPieces }) => {
    const getProgressClass = (percent) => {
        if (percent >= 100) return 'percent-100';
        if (percent >= 90) return 'percent-90';
        if (percent >= 80) return 'percent-80';
        if (percent >= 70) return 'percent-70';
        if (percent >= 60) return 'percent-60';
        if (percent >= 50) return 'percent-50';
        if (percent >= 40) return 'percent-40';
        if (percent >= 30) return 'percent-30';
        if (percent >= 20) return 'percent-20';
        if (percent >= 10) return 'percent-10';
        return 'percent-0';
    };

    const showTakenNumbers = takenPieces > 0 && takenPieces < totalPieces;
    const showLearnedNumbers = learnedPieces > 0 && learnedPieces < totalPieces;

    return (
        <div className="aher-book-progress-wrapper">
            <div className="progress-container">
                <span className="progress-message">נתפסו{showTakenNumbers && ` ${takenPieces}/${totalPieces}`}</span>
                <div className={`progress green ${getProgressClass(takenPercent)}`}>
                    <span className="progress-left">
                        <span className="progress-bar"></span>
                    </span>
                    <span className="progress-right">
                        <span className="progress-bar"></span>
                    </span>
                    <div className="progress-value">{takenPercent}%</div>
                </div>
            </div>
            <div className="progress-container">
                <span className="progress-message">נלמדו{showLearnedNumbers && ` ${learnedPieces}/${totalPieces}`}</span>
                <div className={`progress green ${getProgressClass(learnedPercent)}`}>
                    <span className="progress-left">
                        <span className="progress-bar"></span>
                    </span>
                    <span className="progress-right">
                        <span className="progress-bar"></span>
                    </span>
                    <div className="progress-value">{learnedPercent}%</div>
                </div>
            </div>
        </div>
    );
};

// Tree node component for displaying book structure
const TreeNode = ({ node, learningPiecesMap, usersMap, depth = 0, onLeafClick, onUserClick, filterType }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showLearners, setShowLearners] = useState(false);
    const hasChildren = node.children && node.children.length > 0;
    const isLeaf = !hasChildren;

    // Collect unique learners under this node
    const getNodeUsers = (n) => {
        const usersById = {};
        if (!n.children || n.children.length === 0) {
            const piece = learningPiecesMap[n.id];
            if (piece?.taken) {
                const user = usersMap[n.id];
                if (user) usersById[user.id] = user;
            }
        } else {
            n.children?.forEach(child => {
                const childUsers = getNodeUsers(child);
                Object.assign(usersById, childUsers);
            });
        }
        return usersById;
    };

    const nodeUsers = hasChildren ? Object.values(getNodeUsers(node)) : [];

    // Check if this leaf node is taken and/or learned
    const pieceData = isLeaf ? learningPiecesMap[node.id] : null;
    const isTaken = pieceData?.taken;
    const isLearned = pieceData?.learned;

    // Get user who took this piece
    const pieceUser = isLeaf && isTaken ? usersMap[node.id] : null;

    // Calculate stats for parent nodes
    const getNodeStats = (n) => {
        if (!n.children || n.children.length === 0) {
            const piece = learningPiecesMap[n.id];
            const taken = piece?.taken ? 1 : 0;
            const learned = piece?.learned ? 1 : 0;
            return { total: 1, taken, learned };
        }
        let total = 0;
        let taken = 0;
        let learned = 0;
        n.children.forEach(child => {
            const childStats = getNodeStats(child);
            total += childStats.total;
            taken += childStats.taken;
            learned += childStats.learned;
        });
        return { total, taken, learned };
    };

    const stats = hasChildren ? getNodeStats(node) : null;

    const typeLabels = {
        nose: "נושא",
        helek: "חלק",
        kereh: "כרך",
        siman: "סימן",
        saif: "סעיף",
        perek: "פרק",
        daf: "דף",
        mizmor: "מזמור",
        tora: "תורה",
        shaar: "שער",
        masehet: "מסכת",
        sefer: "ספר",
        halahot: "הלכות",
        seder: "סדר",
        parashat: "פרשת",
        mishna: "משנה",
        alia: "עליה"
    };

    const getNodeDisplayName = () => {
        let name = node.name || '';
        if (node.type === 'daf' && node.dafFrom && node.dafTo) {
            name += ` (${node.dafFrom} - ${node.dafTo})`;
        }
        if (node.type === 'siman' && node.simanFrom && node.simanTo) {
            name += ` (${node.simanFrom} - ${node.simanTo})`;
        }
        return name;
    };

    const getNodeHeaderClass = () => {
        let cls = 'aher-tree-node-header';
        if (isLeaf) {
            cls += ' aher-tree-leaf aher-tree-clickable';
        } else {
            cls += ' aher-tree-parent';
        }
        return cls;
    };

    const handleLeafClick = (e) => {
        e.stopPropagation();
        if (isLeaf && onLeafClick) {
            onLeafClick({
                node,
                isTaken,
                isLearned,
                user: pieceUser,
                displayName: getNodeDisplayName(),
                typeName: typeLabels[node.type] || node.type
            });
        }
    };

    const getLeafStatusIcon = () => {
        if (isLearned) {
            return (
                <span className="aher-tree-leaf-icon aher-icon-learned">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                </span>
            );
        }
        if (isTaken) {
            return (
                <span className="aher-tree-leaf-icon aher-icon-taken">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                </span>
            );
        }
        return (
            <span className="aher-tree-leaf-icon aher-icon-available">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="8"></circle>
                </svg>
            </span>
        );
    };

    const getLeafStatusBadge = () => {
        if (isLearned) {
            return <span className="aher-tree-learned-badge">✓ נלמד</span>;
        }
        if (isTaken) {
            return <span className="aher-tree-taken-badge">נתפס</span>;
        }
        return <span className="aher-tree-available-badge">פנוי</span>;
    };

    const getParentStatusBadges = () => {
        if (!stats) return null;

        if (filterType === 'stillFree') {
            return (
                <span className="aher-tree-stats">
                    <span className="badge aher-badge-taken">{stats.total - stats.taken} נותרו פנויים</span>
                </span>
            );
        } else if (filterType === 'notLearnedYet') {
            return (
                <span className="aher-tree-stats">
                    {stats.taken < stats.total && <span className="badge aher-badge-taken">{stats.total - stats.taken} נותרו פנויים</span>}
                    {stats.taken === stats.total && <span className="badge aher-badge-taken">הכל נתפס!</span>}
                    <span className="badge text-bg-primary aher-badge-learned last-badge">{stats.total} טרם נלמדו</span>
                </span>
            );
        } else if (filterType === 'learned') {
            return (
                <span className="aher-tree-stats">
                    <span className="badge text-bg-primary aher-badge-learned last-badge">{stats.learned} נלמדו</span>
                </span>
            );
        } else {
            return (
                <span className="aher-tree-stats">
                    {stats.taken < stats.total && <span className="badge aher-badge-taken">{stats.taken}/{stats.total} נתפסו</span>}
                    {stats.taken === stats.total && <span className="badge aher-badge-taken">הכל נתפס!</span>}
                    {stats.learned < stats.total && <span className="badge text-bg-primary aher-badge-learned last-badge">{stats.learned}/{stats.total} נלמדו</span>}
                    {stats.learned === stats.total && <span className="badge text-bg-primary aher-badge-learned last-badge">הדרן עלך!</span>}
                </span>
            );
        }
    };

    return (
        <div className={`aher-tree-node ${hasChildren ? 'aher-tree-node-parent' : ''}`} style={{ marginRight: `${depth * 15}px` }}>
            <div
                className={getNodeHeaderClass()}
                onClick={isLeaf ? handleLeafClick : () => setIsExpanded(!isExpanded)}
                style={hasChildren ? { position: 'sticky', top: `0px`, zIndex: 10 + depth } : {}}
            >
                {hasChildren && (
                    <span className={`aher-tree-expand-icon ${isExpanded ? 'aher-expanded' : ''}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </span>
                )}
                {isLeaf && getLeafStatusIcon()}
                <span className="aher-tree-type-badge">{typeLabels[node.type] || node.type}</span>
                <span className="aher-tree-node-name">{getNodeDisplayName()}</span>
                {hasChildren && getParentStatusBadges()}
                {isLeaf && getLeafStatusBadge()}
            </div>
            {hasChildren && isExpanded && (
                <div className="aher-tree-children">
                    {nodeUsers.length > 0 && (
                        <div className="aher-tree-learners-section" style={{ marginRight: `${(depth + 2) * 15}px` }}>
                            <div
                                className="aher-tree-learners-toggle"
                                onClick={() => setShowLearners(!showLearners)}
                            >
                                <span className={`aher-tree-expand-icon ${showLearners ? 'aher-expanded' : ''}`}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                    <circle cx="9" cy="7" r="4"/>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                </svg>
                                <span className="aher-tree-learners-label">לומדים ({nodeUsers.length})</span>
                            </div>
                            {showLearners && (
                                <ul className="aher-tree-learners-list">
                                    {nodeUsers.map(user => (
                                        <li
                                            key={user.id}
                                            className="aher-tree-learner-item"
                                            onClick={(e) => { e.stopPropagation(); onUserClick && onUserClick(user); }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                                <circle cx="12" cy="7" r="4"/>
                                            </svg>
                                            <span>{user.name}</span>
                                            <span className="badge bg-primary aher-learner-badge">{user.learnedCount}/{user.piecesCount}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    {node.children.map((child, idx) => (
                        <TreeNode
                            key={child.id || idx}
                            node={child}
                            learningPiecesMap={learningPiecesMap}
                            usersMap={usersMap}
                            depth={depth + 1}
                            onLeafClick={onLeafClick}
                            onUserClick={onUserClick}
                            filterType={filterType}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const BookStatus = ({ mappingData, learningPieces, sium, siumId, isDisplayMode = false, onShareDisplay }) => {
    const [expandAll, setExpandAll] = useState(false);
    const [collapseKey, setCollapseKey] = useState(0);
    const [selectedPiece, setSelectedPiece] = useState(null);
    const [usersMap, setUsersMap] = useState({});
    const [usersLoading, setUsersLoading] = useState(true);
    const [optDeleteUser, setOptDeleteUser] = useState(null);
    const [filterType, setFilterType] = useState('All'); // '' = no filter, 'stillFree', 'notLearnedYet', 'learned'

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Fetch users for this sium
    useEffect(() => {
        const fetchUsers = async () => {
            if (!siumId) {
                setUsersLoading(false);
                return;
            }

            try {
                const q = query(collection(db, "usersAher"), where("siumId", "==", siumId));
                const querySnapshot = await getDocs(q);

                // Create a map of pieceId -> user
                const pieceToUserMap = {};
                querySnapshot.forEach((doc) => {
                    const userData = doc.data();
                    const odUserId = doc.id;
                    const userPieces = userData.pieces || {};

                    // Calculate user stats
                    const piecesCount = Object.keys(userPieces).length;
                    const learnedCount = Object.values(userPieces).filter(v => v === true).length;

                    Object.keys(userPieces).forEach(pieceId => {
                        pieceToUserMap[pieceId] = {
                            id: odUserId,
                            name: userData.fullName,
                            phone: userData.phone,
                            email: userData.email,
                            nishmat: userData.nishmat,
                            piecesCount,
                            learnedCount
                        };
                    });
                });

                setUsersMap(pieceToUserMap);
            } catch (error) {
                console.error("Error fetching users:", error);
            } finally {
                setUsersLoading(false);
            }
        };

        fetchUsers();
    }, [siumId]);

    // Create a map for quick lookup of learning pieces by ID
    const learningPiecesMap = {};
    if (learningPieces) {
        learningPieces.forEach(piece => {
            learningPiecesMap[piece.id] = piece;
        });
    }

    // Calculate totals
    const totalPieces = learningPieces?.length || 0;
    const takenPieces = learningPieces?.filter(p => p.taken).length || 0;
    const learnedPieces = learningPieces?.filter(p => p.learned).length || 0;
    const takenPercent = totalPieces > 0 ? Math.floor((takenPieces / totalPieces) * 100) : 0;
    const learnedPercent = totalPieces > 0 ? Math.floor((learnedPieces / totalPieces) * 100) : 0;

    const handleExpandAll = () => {
        setExpandAll(true);
        setCollapseKey(prev => prev + 1);
    };

    const handleCollapseAll = () => {
        setExpandAll(false);
        setCollapseKey(prev => prev + 1);
    };

    // Handle filter changes
    const handleFilterChange = (newFilterType) => {
        // If clicking the same radio button, deselect it
        if (filterType === newFilterType) {
            setFilterType('All');
        } else {
            setFilterType(newFilterType);
        }
    };

    // Filter nodes based on criteria
    const filterTreeData = (nodes) => {
        if (!filterType) return nodes;

        const filterNode = (node) => {
            // For leaf nodes, check the filter criteria
            if (!node.children || node.children.length === 0) {
                const piece = learningPiecesMap[node.id];
                const isTaken = piece?.taken;
                const isLearned = piece?.learned;

                if (filterType === 'stillFree' && isTaken) return null;
                if (filterType === 'notLearnedYet' && isLearned) return null;
                if (filterType === 'learned' && !isLearned) return null;

                return node;
            }

            // For parent nodes, filter children recursively
            const filteredChildren = node.children
                .map(child => filterNode(child))
                .filter(child => child !== null);

            // If no children pass the filter, hide this parent too
            if (filteredChildren.length === 0) return null;

            return { ...node, children: filteredChildren };
        };

        return nodes.map(node => filterNode(node)).filter(node => node !== null);
    };

    const handleLeafClick = (pieceInfo) => {
        setSelectedPiece(pieceInfo);
    };

    const handleUserClick = (user) => {
        setSelectedPiece({ isTaken: true, user });
    };

    // Display user pieces as a tree (same style as learnersListAher)
    const displayUserPieces = (userId) => {
        if (!mappingData || !mappingData.treeData) return <p>טוען נתוני מיפוי...</p>;

        // Collect all pieces for this user
        const userPiecesStatus = {};
        Object.entries(usersMap).forEach(([pieceId, user]) => {
            if (user.id === userId) {
                const piece = learningPiecesMap[pieceId];
                userPiecesStatus[pieceId] = piece?.learned || false;
            }
        });
        const pieceIds = Object.keys(userPiecesStatus);
        if (pieceIds.length === 0) return <p>אין חלקים</p>;

        // Build a tree containing only the user's pieces and their parents
        const buildUserTree = (nodes) => {
            if (!nodes || !Array.isArray(nodes)) return [];
            const result = [];
            for (const node of nodes) {
                if (!node) continue;
                const hasChildren = node.children && node.children.length > 0;
                if (hasChildren) {
                    const childTree = buildUserTree(node.children);
                    if (childTree.length > 0) {
                        let totalCount = 0, learnedCount = 0;
                        const countStats = (children) => {
                            children.forEach(child => {
                                if (child.isLeaf) { totalCount++; if (child.isLearned) learnedCount++; }
                                else if (child.children) countStats(child.children);
                            });
                        };
                        countStats(childTree);
                        result.push({ ...node, children: childTree, isLeaf: false, totalCount, learnedCount });
                    }
                } else if (node.id && pieceIds.includes(node.id)) {
                    result.push({ ...node, isLeaf: true, isLearned: userPiecesStatus[node.id] === true });
                }
            }
            return result;
        };

        const userTree = buildUserTree(mappingData.treeData);
        if (userTree.length === 0) return <p>לא נמצאו חלקים תואמים במיפוי</p>;

        const typeLabels = {
            nose: "נושא", helek: "חלק", kereh: "כרך", siman: "סימן", saif: "סעיף",
            perek: "פרק", daf: "דף", mizmor: "מזמור", tora: "תורה", shaar: "שער",
            masehet: "מסכת", sefer: "ספר", halahot: "הלכות", seder: "סדר",
            parashat: "פרשת", mishna: "משנה", alia: "עליה"
        };

        const TreeNodeComponent = ({ node, depth = 0 }) => {
            const [isExpanded, setIsExpanded] = React.useState(false);
            const hasChildren = node.children && node.children.length > 0;
            const isLeaf = node.isLeaf;

            const getNodeDisplayName = () => {
                let name = node.name || '';
                if (node.type === 'daf' && node.dafFrom && node.dafTo) name += ` (${node.dafFrom} - ${node.dafTo})`;
                if (node.type === 'siman' && node.simanFrom && node.simanTo) name += ` (${node.simanFrom} - ${node.simanTo})`;
                return name;
            };

            return (
                <div className={`user-piece-tree-node ${!isLeaf ? 'parent-node' : ''}`} style={{ marginRight: `${depth * 15}px` }}>
                    <div
                        className={`user-piece-tree-header ${isLeaf ? (node.isLearned ? 'learned' : 'not-learned') : 'parent'}`}
                        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
                        style={{ cursor: hasChildren ? 'pointer' : 'default' }}
                    >
                        {isLeaf ? (
                            node.isLearned ? (
                                <span className="piece-status-icon learned">✓</span>
                            ) : (
                                <span className="piece-status-icon not-learned">○</span>
                            )
                        ) : (
                            <span className={`piece-parent-icon ${isExpanded ? 'expanded' : ''}`}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </span>
                        )}
                        <span className="piece-type-badge">{typeLabels[node.type] || node.type}</span>
                        <span className={`piece-node-name ${!isLeaf ? 'parent-name' : ''}`}>{getNodeDisplayName()}</span>
                        {!isLeaf && (
                            <span className="piece-stats">
                                <span className="badge bg-primary">נלמדו {node.learnedCount}/{node.totalCount}</span>
                            </span>
                        )}
                    </div>
                    {hasChildren && isExpanded && (
                        <div className="user-piece-tree-children">
                            {node.children.map(child => (
                                <TreeNodeComponent key={child.id} node={child} depth={depth + 1} />
                            ))}
                        </div>
                    )}
                </div>
            );
        };

        return (
            <div className="user-pieces-tree">
                {userTree.map(node => (
                    <TreeNodeComponent key={node.id} node={node} depth={0} />
                ))}
            </div>
        );
    };

    const closeModal = () => {
        setSelectedPiece(null);
    };

    const getWhatsAppLink = (userObj, includeLink) => {
        const countryCode = sium?.phonePrefix || '972';
        let cleanPhone = userObj.phone.replace(/\D/g, '');
        if (cleanPhone.startsWith(countryCode)) {
            cleanPhone = cleanPhone.substring(countryCode.length);
        }
        if (cleanPhone.startsWith('0')) {
            cleanPhone = cleanPhone.substring(1);
        }
        const phone = countryCode + cleanPhone;

        let message = `שלום ${userObj.name}`;
        if (includeLink) {
            message += `\n\nניתן לעדכן את מצב הלמידה שלך בקישור הבא:\nhttps://siumshas.co.il/userStatusAher/${userObj.id}`;
        }
        return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    };

    const shareUserPage = (userObj) => {
        if (navigator.share) {
            const bookName = mappingData?.bookName || 'ספר';
            navigator.share({
                title: `סיום ${bookName} של "${sium.kehilaName}"`,
                text: 'מצב הלמידה של ' + userObj.name,
                url: '/userStatusAher/' + userObj.id,
            });
        }
    };

    // User actions like in siumMasehtotStatus - only show when not in display mode
    const getUserActions = (userObj) => {
        if (isDisplayMode) return null;

        return (
            <div className="margin-top-20 d-flex flex-wrap">
                <div className="dropdown-center sium-button-container">
                    <button className="btn btn-secondary dropdown-toggle sium-status-button dark-button" type="button"
                            data-bs-toggle="dropdown" aria-expanded="false">
                        פעולות בדף הלומד
                    </button>
                    <ul className="dropdown-menu lomed-actions-list">
                        <li className="dropdown_item-1"><a className="btn sium-status-button" href={"/userStatusAher/" + userObj.id}>מעבר לדף הלומד</a></li>
                        <li className="dropdown_item-2"><a className="btn sium-status-button" href={"/editUserAher/" + userObj.id}>עריכה</a></li>
                        <li className="dropdown_item-3"><button className="btn sium-status-button" onClick={() => setOptDeleteUser(userObj)}>מחיקה</button></li>
                    </ul>
                </div>
                <div className="dropdown-center sium-button-container">
                    <button className="btn btn-secondary dropdown-toggle sium-status-button dark-button" type="button"
                            data-bs-toggle="dropdown" aria-expanded="false">
                        שיתוף דף הלומד
                    </button>
                    <ul className="dropdown-menu lomed-actions-list">
                        <li className="dropdown_item-1"><button className="btn sium-status-button" onClick={() => shareUserPage(userObj)}>שיתוף קישור</button></li>
                        {userObj.phone && <li className="dropdown_item-2"><a className="btn sium-status-button" href={getWhatsAppLink(userObj, true)} target="_blank" rel="noopener noreferrer">שיתוף ב-WhatsApp</a></li>}
                        {userObj.email && isMobile && <li className="dropdown_item-3"><a className="btn sium-status-button" href={`mailto:${userObj.email}?subject=${encodeURIComponent('דף הסטטוס שלך')}&body=${encodeURIComponent('שלום ' + userObj.name + ',\n\nניתן לעדכן את מצב הלמידה שלך בקישור הבא:\nhttps://siumshas.co.il/userStatusAher/' + userObj.id)}`}>שיתוף במייל</a></li>}
                    </ul>
                </div>
                {(userObj.phone || (isMobile && userObj.email)) && <div className="dropdown-center sium-button-container">
                    <button className="btn btn-secondary dropdown-toggle sium-status-button dark-button" type="button"
                            data-bs-toggle="dropdown" aria-expanded="false">
                        יצירת קשר עם הלומד
                    </button>
                    <ul className="dropdown-menu lomed-actions-list">
                        {userObj.phone && <li className="dropdown_item-1"><a className="btn sium-status-button" href={"tel:+972" + userObj.phone}>להתקשר ללומד</a></li>}
                        {userObj.phone && <li className="dropdown_item-2"><a className="btn sium-status-button" href={"sms:+972" + userObj.phone}>SMS</a></li>}
                        {userObj.phone && <li className="dropdown_item-3"><a className="btn sium-status-button" href={getWhatsAppLink(userObj, false)} target="_blank" rel="noopener noreferrer">WhatsApp</a></li>}
                        {userObj.email && isMobile && <li className="dropdown_item-4"><a className="btn sium-status-button" href={`mailto:${userObj.email}`}>Email</a></li>}
                    </ul>
                </div>}
            </div>
        );
    };

    if (!mappingData || !mappingData.treeData) {
        return (
            <div className="aher-book-status-empty">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                </svg>
                <p>לא נמצא מיפוי ספר</p>
            </div>
        );
    }

    return (
        <div className="aher-book-status-container">
            <div className="aher-book-status-header">
                <h3 className="aher-book-status-title">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1F2B70" strokeWidth="2">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                    </svg>
                    {mappingData.bookName}
                </h3>
            </div>

            <SiumAherProgress
                takenPercent={takenPercent}
                totalPieces={totalPieces}
                takenPieces={takenPieces}
                learnedPercent={learnedPercent}
                learnedPieces={learnedPieces}
            />

            {/* Share display button - only when not in display mode */}
            {!isDisplayMode && onShareDisplay && (
                <div className="text-center margin-bottom-10">
                    <button className="btn dark-button share-display-sium" onClick={onShareDisplay}>
                        <span>שתף את מצב הלימוד</span>
                    </button>
                </div>
            )}

            {/* Learner login button - only in display mode */}
            {isDisplayMode && (
                <div className="aher-learner-login-container" style={{ textAlign: 'center', marginBottom: '15px' }}>
                    <a
                        href={"/lomdimLoginAher/" + siumId}
                        className="btn dark-button share-display-sium"
                    >
                        כניסה ללומדים
                    </a>
                </div>
            )}

            <div className="aher-book-status-legend">
                <div className="aher-legend-item">
                    <span>נלמד</span>
                    <span className="aher-legend-icon aher-legend-learned">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                    </span>
                </div>
                <div className="aher-legend-item">
                    <span className="aher-legend-icon aher-legend-taken">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M20 6L9 17l-5-5"></path>
                        </svg>
                    </span>
                    <span>נתפס</span>
                </div>
                <div className="aher-legend-item">
                    <span className="aher-legend-icon aher-legend-available">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="8"></circle>
                        </svg>
                    </span>
                    <span>פנוי</span>
                </div>
            </div>

            {/* Filter checkboxes */}
            <div className="masehtot-filter-container margin-top-10 margin-bottom-10">
                <label className="masehtot-filter-label">סינון:</label>
                <div className="form-check form-check-inline">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="filterAher"
                        id="filterAllAher"
                        checked={filterType === 'All'}
                        onChange={() => handleFilterChange('All')}
                    />
                    <label className="form-check-label" htmlFor="filterAllAher">
                        הכל
                    </label>
                </div>
                <div className="form-check form-check-inline">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="filterAher"
                        id="filterStillFreeAher"
                        checked={filterType === 'stillFree'}
                        onChange={() => handleFilterChange('stillFree')}
                    />
                    <label className="form-check-label" htmlFor="filterStillFreeAher">
                        פנוי
                    </label>
                </div>
                <div className="form-check form-check-inline">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="filterAher"
                        id="filterNotLearnedYetAher"
                        checked={filterType === 'notLearnedYet'}
                        onChange={() => handleFilterChange('notLearnedYet')}
                    />
                    <label className="form-check-label" htmlFor="filterNotLearnedYetAher">
                        טרם נלמד
                    </label>
                </div>
                <div className="form-check form-check-inline">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="filterAher"
                        id="filterLearnedAher"
                        checked={filterType === 'learned'}
                        onChange={() => handleFilterChange('learned')}
                    />
                    <label className="form-check-label" htmlFor="filterLearnedAher">
                        כבר נלמד
                    </label>
                </div>
            </div>

            <div className="aher-tree-container" key={collapseKey}>
                {filterTreeData(mappingData.treeData).map((node, idx) => (
                    <TreeNode
                        key={node.id || idx}
                        node={node}
                        learningPiecesMap={learningPiecesMap}
                        usersMap={usersMap}
                        depth={0}
                        onLeafClick={handleLeafClick}
                        onUserClick={handleUserClick}
                        filterType={filterType}
                    />
                ))}
            </div>

            {/* Modal for free piece - Add User (show in both display and non-display mode) */}
            {selectedPiece && !selectedPiece.isTaken && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" onClick={closeModal}>
                    <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title fs-5">הדף טרם נתפס</h1>
                                <button type="button" className="btn-close" onClick={closeModal} aria-label="Close"></button>
                            </div>
                            <div className="modal-body sium-status-options-container add-user-modal">
                                <span>תרצה להוסיף לומד חדש?</span>
                                <div className="sium-status-option" onClick={() => window.location = "/addUserAher/" + siumId} tabIndex={0}>
                                    <svg fill="#414a87" height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 511.999 511.999">
                                        <g>
                                            <g>
                                                <path d="M503.292,210.719H398.127l-2.721-33.724c-0.005-0.053-0.015-0.104-0.021-0.157c-0.003-0.031-0.007-0.062-0.01-0.092 l-8.003-99.08c-0.365-4.522-4.141-8.007-8.68-8.007H133.303c-4.537,0-8.314,3.484-8.68,8.007l-2.457,30.415 c-0.387,4.794,3.185,8.994,7.979,9.381c4.807,0.389,8.993-3.185,9.381-7.979l1.81-22.408h229.323l6.617,81.912h-16.777 c-4.81,0-8.708,3.899-8.708,8.708s3.898,8.708,8.708,8.708h18.195l1.962,24.312H131.344l1.966-24.312h198.163 c4.81,0,8.708-3.899,8.708-8.708s-3.898-8.708-8.708-8.708H134.718l2.356-29.139c0.387-4.794-3.185-8.994-7.979-9.381 c-4.803-0.385-8.994,3.186-9.381,7.979l-5.845,72.273H8.708c-4.809,0-8.708,3.899-8.708,8.708s3.899,8.708,8.708,8.708h101.747 c-1.457,1.996-3.484,3.93-6.064,6.31c-6.083,5.611-14.414,13.295-14.414,27.819c0,14.525,8.331,22.209,14.414,27.82 c5.787,5.339,8.807,8.43,8.807,15.019c0,6.588-3.02,9.68-8.807,15.018c-6.083,5.611-14.414,13.295-14.414,27.82 s8.331,22.21,14.414,27.822c5.787,5.339,8.807,8.432,8.807,15.022c0,6.59-3.02,9.683-8.807,15.022 c-6.083,5.611-14.414,13.298-14.414,27.822c0,4.809,3.899,8.708,8.708,8.708s8.708-3.899,8.708-8.708 c0-6.59,3.02-9.682,8.807-15.022c6.083-5.611,14.414-13.297,14.414-27.822c0-14.525-8.331-22.211-14.414-27.822 c-5.787-5.339-8.807-8.432-8.807-15.022c0-6.588,3.02-9.681,8.807-15.019c6.084-5.611,14.414-13.295,14.414-27.819 c0-14.525-8.331-22.209-14.414-27.82c-5.787-5.339-8.807-8.43-8.807-15.019c0-6.588,3.02-9.68,8.807-15.018 c4.73-4.363,10.804-9.998,13.278-19.111h253.047c2.474,9.113,8.548,14.748,13.278,19.111c5.787,5.339,8.807,8.43,8.807,15.018 c0,6.589-3.02,9.681-8.807,15.019c-6.083,5.611-14.414,13.296-14.414,27.82s8.332,22.209,14.414,27.819 c5.787,5.339,8.807,8.43,8.807,15.019c0,6.59-3.02,9.682-8.807,15.022c-6.083,5.611-14.414,13.298-14.414,27.822 c0,14.525,8.332,22.211,14.414,27.822c5.787,5.339,8.807,8.432,8.807,15.022c0,4.809,3.898,8.708,8.708,8.708 c4.81,0,8.708-3.899,8.708-8.708c0-14.525-8.331-22.21-14.413-27.822c-5.788-5.339-8.808-8.432-8.808-15.022 c0-6.59,3.02-9.683,8.808-15.022c6.083-5.611,14.413-13.298,14.413-27.822s-8.332-22.209-14.414-27.82 c-5.787-5.339-8.807-8.43-8.807-15.018c0-6.589,3.02-9.681,8.807-15.019c6.083-5.611,14.414-13.296,14.414-27.82 c0-14.524-8.332-22.209-14.414-27.819c-2.58-2.38-4.607-4.315-6.064-6.31h101.747c4.81,0,8.708-3.899,8.708-8.708 S508.102,210.719,503.292,210.719z"></path>
                                            </g>
                                        </g>
                                    </svg>
                                    <span>הוסף לומד</span>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeModal}>ביטול</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for taken piece - User Details (hide actions in display mode) */}
            {selectedPiece && selectedPiece.isTaken && selectedPiece.user && !isDisplayMode && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" onClick={closeModal}>
                    <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title fs-5">
                                    {selectedPiece.user.name}
                                    <span className="badge bg-primary margin-right-10">
                                        {selectedPiece.user.learnedCount}/{selectedPiece.user.piecesCount} חלקים נלמדו
                                    </span>
                                </h1>
                                <button type="button" className="btn-close" onClick={closeModal} aria-label="Close"></button>
                            </div>
                            <div className="modal-body sium-status-options-container check-user-modal">
                                {selectedPiece.user.phone && (
                                    <div className="user-detail-modal check-user-modal">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                        </svg>
                                        {selectedPiece.user.phone}
                                    </div>
                                )}
                                {selectedPiece.user.email && (
                                    <div className="user-detail-modal mt-2 check-user-modal">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                            <polyline points="22,6 12,13 2,6"></polyline>
                                        </svg>
                                        {selectedPiece.user.email}
                                    </div>
                                )}
                                {getUserActions(selectedPiece.user)}
                                {/* User's pieces tree */}
                                <div className="user-pieces-in-bookstatus">
                                    <h5 className="user-pieces-header">החלקים של הלומד ({selectedPiece.user.piecesCount})</h5>
                                    {displayUserPieces(selectedPiece.user.id)}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeModal}>ביטול</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for taken piece in display mode - Simple view with just name */}
            {selectedPiece && selectedPiece.isTaken && selectedPiece.user && isDisplayMode && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" onClick={closeModal}>
                    <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title fs-5">
                                    {selectedPiece.user.name}
                                    <span className="badge bg-primary margin-right-10">
                                        {selectedPiece.user.learnedCount}/{selectedPiece.user.piecesCount} חלקים נלמדו
                                    </span>
                                </h1>
                                <button type="button" className="btn-close" onClick={closeModal} aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <div>
                                    <h5 className="user-pieces-header">החלקים של הלומד ({selectedPiece.user.piecesCount})</h5>
                                    {displayUserPieces(selectedPiece.user.id)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {optDeleteUser && !isDisplayMode && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title fs-5">מחיקת לומד</h1>
                                <button type="button" className="btn-close" onClick={() => setOptDeleteUser(null)} aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <p>האם אתה בטוח שברצונך למחוק את הלומד <strong>{optDeleteUser.name}</strong>?</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setOptDeleteUser(null)}>ביטול</button>
                                <button type="button" className="btn btn-danger" onClick={() => {
                                    // Navigate to learners list where deletion can happen
                                    window.location.reload();
                                }}>מחק</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookStatus;

